import { Fragment } from '@atlaskit/editor-prosemirror/model';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';

import { aiSessionContextPluginKey } from '../../ai-session-context/ai-session-context-plugin-key';
import { aiExperienceDecorationPluginKey } from '../../decoration/decoration-plugin-key';
import type { PurePmInEditorDirectStreamingPluginState } from '../types';

/**
 * Mutable state container for markdown streaming event publishing.
 * Tracks which response version has been published to avoid duplicate events.
 */
export interface MarkdownStreamingViewState {
	lastPublishedVersion: number | null;
}

/**
 * Creates the initial markdown streaming view state.
 */
export function createMarkdownStreamingViewState(): MarkdownStreamingViewState {
	return {
		lastPublishedVersion: null,
	};
}

/**
 * Handles publishing events during markdown streaming flows.
 *
 * Markdown streaming responses provide plain text (not ADF) and apply changes
 * outside the ProseMirror document. This view-update handler converts the markdown
 * to a ProseMirror fragment and publishes the appropriate event.
 *
 * Currently used by:
 * - Suggest title (Confluence): publishes eventHub event to update the page title textarea.
 */
export function handleMarkdownStreaming(
	view: EditorView,
	state: PurePmInEditorDirectStreamingPluginState,
	markdownStreamingState: MarkdownStreamingViewState,
): void {
	// Only process when actively streaming or just finished streaming
	if (!state.isStreaming && !state.isStreamingFinished) {
		return;
	}

	const decorationState = aiExperienceDecorationPluginKey.getState(view.state);
	if (!decorationState?.configItem) {
		return;
	}

	// Only handle config items that stream markdown
	if (!decorationState.configItem.streamsMarkdown) {
		return;
	}

	const sessionState = aiSessionContextPluginKey.getState(view.state);
	if (!sessionState?.markdown) {
		return;
	}

	// Skip if we already published this version
	if (
		markdownStreamingState.lastPublishedVersion !== null &&
		sessionState.responseVersion === markdownStreamingState.lastPublishedVersion
	) {
		return;
	}

	const { onMarkdownStreamUpdate } = decorationState.configItem;
	if (!onMarkdownStreamUpdate) {
		return;
	}

	// Create a ProseMirror fragment from the markdown text
	const textNode = view.state.schema.text(sessionState.markdown);
	const fragment = Fragment.from(textNode);

	const isFinal = sessionState.status === 'complete';

	// Delegate to the config item's handler to apply the markdown change.
	// Each config item defines how its streamed markdown is used — e.g. suggest title
	// publishes an eventHub event to update the Confluence page title textarea.
	onMarkdownStreamUpdate({
		editorView: view,
		pmFragment: fragment,
		isFinal,
	});

	// Track published version to avoid duplicates
	markdownStreamingState.lastPublishedVersion = sessionState.responseVersion ?? null;
}
